import {Component, OnDestroy, HostListener, OnInit} from '@angular/core';
import {RouterOutlet} from '@angular/router';
import {TranslateModule} from '@ngx-translate/core';
import {DomSanitizer} from '@angular/platform-browser';

import {UtilsPL2 as U} from '@common/utils/dist/utils/utils-pl2.js';

import {ReplaySubject} from 'rxjs';
import {take, takeUntil} from 'rxjs/operators';

import {MatIconRegistry} from '@angular/material/icon';

import {environment} from '../environments/environment';
import {environment as prodEnv} from '../environments/environment.prod';

import {ConnectionStore} from '@stores/connection.store';
import {EntryStore} from '@stores/entry.store';
import {ErrorReportStore} from '@stores/error-report.store';
import {UserStore} from '@stores/user.store';
import {APIState, APIStateStore} from './stores/api-state.store';

import {ConnectionService} from '@services/connection.service';
import {TranslateService} from '@ngx-translate/core';
import {WebsocketService} from '@services/websocket.service';

import {UserReactor} from './reactors/user.reactor';
import {EntryReactor} from './reactors/entry.reactor';
import {ErrorReportReactor} from './reactors/error-report-api.reactor';

import {userChangeFilter} from '@utils/data-view-pipes';
import {TrackedEventReactor} from './reactors/tracked-event.reactor';
import {UserDetailsReactor} from './reactors/user-details.reactor';
import {
  DialogRendererService,
  DialogType,
} from './components/dialogs/dialog-renderer.service';
import {PosthogRouteReactor} from './reactors/posthog-route.reactor';
import {PosthogUserReactor} from './reactors/posthog-user.reactor';
import {PosthogDataConfigReactor} from './reactors/posthog-data-config.reactor';
import {LessonChatReactor} from './reactors/lesson-chat.reactor';
import {LessonGenerationReactor} from './reactors/lesson-generation.reactor';
import {VideoTutorialComponent} from './components/video-tutorial/video-tutorial.component';
import {DebugConsoleComponent} from './components/debug-console/debug-console.component';
import {PolicyDirective} from '@directives/policy/policy.directive';
import {LessonGenerationSnackbarsComponent} from '@components/lesson-generation-snackbars/lesson-generation-snackbars.component';
import {MatSnackBar, MatSnackBarModule} from '@angular/material/snack-bar';

@Component({
  standalone: true,
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [
    RouterOutlet,
    TranslateModule,
    VideoTutorialComponent,
    DebugConsoleComponent,
    PolicyDirective,
    MatSnackBarModule,
    LessonGenerationSnackbarsComponent,
  ],
})
export class AppComponent implements OnDestroy, OnInit {
  readonly collapsedStyle = {
    flex: '1 1 6px',
    boxSizing: 'border-box',
    'max-height': '6px',
    'min-height': '6px',
  };
  readonly expandedStyle = {
    flex: '1 1 300px',
    boxSizing: 'border-box',
    'max-height': '300px',
    'min-height': '300px',
  };
  consoleStyle: {[key: string]: string};

  _destroyed$ = new ReplaySubject<boolean>();

  constructor(
    private matIconRegistry: MatIconRegistry,
    private domSanitizer: DomSanitizer,
    private translate: TranslateService,
    private connectionStore: ConnectionStore,
    private connectionService: ConnectionService,
    private entryStore: EntryStore,
    private errorReportStore: ErrorReportStore,
    private dialogRenderer: DialogRendererService,
    private userStore: UserStore,
    private apiStateStore: APIStateStore,
    private snackBar: MatSnackBar,
    // WARNING these services are here to initialize them
    private errorReportApiService: ErrorReportReactor,
    private websocketService: WebsocketService,
    private posthogRouteReactor: PosthogRouteReactor,
    private posthogUserReactor: PosthogUserReactor,
    private posthogDataConfigReactor: PosthogDataConfigReactor,
    private lessonChatReactor: LessonChatReactor,
    private lessonGenerationReactor: LessonGenerationReactor,
    private entryReactor: EntryReactor,
    private userReactor: UserReactor,
    private trackedEventReactor: TrackedEventReactor,
    private userDetailsReactor: UserDetailsReactor,
  ) {
    this.matIconRegistry.addSvgIconResolver((name) =>
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        `assets/icons/${name}.svg`,
      ),
    );
    // this language will be used as a fallback when a translation isn't found in the current language
    translate.setDefaultLang('en');
    const currentLanguage = this.translate.getBrowserLang();
    translate.use(currentLanguage);
  }

  @HostListener('window:beforeunload', ['$event'])
  saveToStorage(event: Event) {
    const isNotSynchronized = this.entryStore.saveToStorage();
    this.errorReportStore.saveToStorage();
    if (isNotSynchronized && !U.isEmpty(event)) {
      event.preventDefault();
      this.dialogRenderer.render({
        type: DialogType.Notification,
        data: {
          title: 'cloudlab.closeConfirmationTitle',
          content: 'cloudlab.closeConfirmationMessage',
        },
      });
      return false;
    }
    this.userStore.saveCIdToStorage();
  }

  @HostListener('window:keyup.shift.control.esc')
  keyUp() {
    this.showConsole();
  }

  @HostListener('window:offline')
  offline() {
    console.log('offline');
    this.apiStateStore.setAPIState(APIState.Offline);
  }

  @HostListener('window:online')
  online() {
    console.log('online');
    this.apiStateStore.setAPIState(APIState.Default);
  }

  ngOnDestroy() {
    if (!!this.connectionStore.state()) {
      this.connectionService.disconnect();
    }
  }

  ngOnInit() {
    this._checkForSwUpdate();
    this.errorReportStore.loadStorage();
    this.userStore.state$
      .pipe(takeUntil(this._destroyed$), userChangeFilter())
      .subscribe((user) => {
        // It shouldn't be necessary to call saveToStorage here as a change in users results in a window reload which
        // triggers it anyway
        // this.entryStore.saveToStorage();
        if (this.consoleStyle !== this.expandedStyle) {
          this.consoleStyle =
            !U.isEmpty(user?.auth) || environment.host !== prodEnv.host
              ? this.collapsedStyle
              : null;
        }
        if (!U.isEmpty(user)) {
          this.entryStore.loadStorage(user.userId);
          if (!U.isEmpty(user.email)) {
            this.userStore.refreshAttributes();
          }
        }
      });
  }

  showConsole() {
    this.consoleStyle = this.expandedStyle;
  }

  closeConsole() {
    this.consoleStyle = null;
  }

  expandConsole() {
    this.consoleStyle = this.expandedStyle;
  }

  collapseConsole() {
    this.consoleStyle = this.collapsedStyle;
  }

  private _checkForSwUpdate() {
    if (sessionStorage?.getItem('pl-sw-updated')) {
      sessionStorage.removeItem('pl-sw-updated');
      this.translate
        .get(['notification.appUpdated', 'common.close'])
        .pipe(take(1))
        .subscribe((msg) => {
          this.snackBar.open(
            msg['notification.appUpdated'],
            msg['common.close'],
            {duration: 5000},
          );
        });
    }
  }

  goToMainContent() {
    const focusElement = document.querySelectorAll(
      '#content',
    )[0] as HTMLElement;
    focusElement?.focus();
  }
}
